import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { SaveRounded as SaveIcon, Add as AddIcon } from '@mui/icons-material';
import { Form } from '../../design';
import { ScenarioData, LineData } from '../../resources/ScenarioResource';
import LineInputForm from './LineInputForm';

type Props = {
  onSubmit: (data: any) => void;
  saving?: boolean;
  data?: ScenarioData;
  error?: string;
  onCancel?: () => void;
};

const makeRequestBody = (formData: any, lines: LineData[]) => {
  return {
    title: formData.title,
    description: formData.description,
    lines: lines
      .filter((line) => line.text && line.participantId) // Only include lines with required fields
      .map((line) => {
        const lineData: any = {
          text: line.text,
          participantId: line.participantId,
        };
        if (line.translation && line.translation.trim()) {
          lineData.translation = line.translation;
        }
        return lineData;
      }),
  };
};

const emptyLine: LineData = {
  text: '',
  translation: '',
  participantId: '',
};

export default function ScenarioForm({ onSubmit, saving, data, error, onCancel }: Props) {
  const [lines, setLines] = useState<LineData[]>(data?.lines || [emptyLine]);

  useEffect(() => {
    setLines(data?.lines || [emptyLine]);
  }, [data?.lines]);

  const handleSubmit = async (formData: any) => {
    const body = makeRequestBody(formData, lines);
    onSubmit(body);
  };

  return (
    <Form onSubmit={handleSubmit} className="flex flex-col gap-4" disabled={saving}>
      <Typography variant="h4" fontWeight="bold">
        Basic Info
      </Typography>
      <Box className="grid grid-cols-1 gap-4">
        <TextField
          label="Title"
          name="title"
          defaultValue={data?.title}
          required
          fullWidth
        />
        <TextField
          label="Description"
          name="description"
          defaultValue={data?.description}
          multiline
          rows={4}
          required
          fullWidth
        />
      </Box>

      <Divider />
      <Box className="flex justify-between items-center">
        <Typography variant="h4" fontWeight="bold">
          Dialogue Lines
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setLines([
              ...lines,
              {
                text: '',
                translation: '',
                participantId: '',
              },
            ]);
          }}
        >
          Add Line
        </Button>
      </Box>

      <Box className="flex flex-col gap-4">
        {lines.map((line, index) => (
          <LineInputForm
            key={index}
            line={line}
            index={index}
            onUpdate={(idx, updatedLine) => {
              const newLines = [...lines];
              newLines[idx] = updatedLine;
              setLines(newLines);
            }}
            onDelete={(idx) => {
              setLines(lines.filter((_, i) => i !== idx));
            }}
            canDelete={lines.length > 1}
          />
        ))}
      </Box>

      <Divider />
      <Box className="flex flex-row gap-4 items-center">
        <Button
          size="large"
          startIcon={
            saving ? <CircularProgress size={24} /> : <SaveIcon sx={{ height: 24, width: 24 }} />
          }
          type="submit"
          variant="contained"
          disabled={saving}
        >
          Save Scenario
        </Button>
        {onCancel && (
          <Button size="large" variant="outlined" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </Form>
  );
}
